/**
 * InstallAppPrompt — Iter144
 *
 * Shows a small "Install app" chip in the TopBar / drawer once the browser
 * signals installability (Chrome/Edge/Android fire `beforeinstallprompt`).
 *
 * iOS Safari never fires that event, so on iOS we detect the platform and
 * show a one-time bottom sheet explaining the manual "Share → Add to Home
 * Screen" flow instead.
 *
 * Dismissals are stored in localStorage so we never nag returning users.
 */
import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share2, X } from "lucide-react";

const DISMISS_KEY = "sf-install-dismissed";

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isAppleMobile = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ pretends to be Mac Safari; sniff touch instead.
  const isModernIPad = ua.includes("Mac") && "ontouchend" in document;
  return isAppleMobile || isModernIPad;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS
    window.navigator.standalone === true
  );
}

export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [iosHint, setIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(DISMISS_KEY); } catch { return false; }
  });

  useEffect(() => {
    if (isStandalone() || dismissed) return;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    // iOS never fires the event — check platform after mount so SSR is safe.
    if (isIOS()) setIosHint(true);
    const onInstalled = () => {
      setDeferred(null);
      setIosHint(false);
      try { localStorage.setItem(DISMISS_KEY, "installed"); } catch { /* ignore */ }
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [dismissed]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, "user"); } catch { /* ignore */ }
    setDismissed(true);
    setDeferred(null);
    setIosHint(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "dismissed") dismiss();
      else {
        setDeferred(null);
        try { localStorage.setItem(DISMISS_KEY, "installed"); } catch { /* ignore */ }
      }
    } catch {
      dismiss();
    }
  }, [deferred, dismiss]);

  if (dismissed) return null;

  // Android / Desktop Chrome / Edge — native install chip
  if (deferred) {
    return (
      <div
        className="fixed bottom-24 lg:bottom-6 left-3 right-3 lg:left-auto lg:right-6 lg:w-96 z-40 rounded-lg border border-border bg-card shadow-lg p-3 flex items-start gap-3"
        data-testid="pwa-install-prompt"
      >
        <div className="w-9 h-9 rounded-md bg-teal text-white flex items-center justify-center shrink-0">
          <Download size={18} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Install SenrizeFlow</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Get one-tap access from your home screen. Works offline for cached pages.
          </div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={install} className="bg-teal hover:bg-teal/90 text-white"
                data-testid="pwa-install-btn">
              Install
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} data-testid="pwa-install-dismiss">
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  // iOS Safari — manual "Share → Add to Home Screen" hint
  if (iosHint) {
    return (
      <div
        className="fixed bottom-24 left-3 right-3 z-40 rounded-lg border border-border bg-card shadow-lg p-3 flex items-start gap-3"
        data-testid="pwa-install-ios-hint"
      >
        <div className="w-9 h-9 rounded-md bg-teal text-white flex items-center justify-center shrink-0">
          <Share2 size={18} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0 text-xs">
          <div className="text-sm font-semibold">Install on iPhone</div>
          <div className="text-muted-foreground mt-0.5">
            Tap the <span className="font-medium">Share</span> button in Safari and choose
            <span className="font-medium"> &ldquo;Add to Home Screen&rdquo;</span>. SenrizeFlow will launch full-screen like a native app.
          </div>
          <div className="mt-2">
            <Button size="sm" variant="ghost" onClick={dismiss} data-testid="pwa-install-ios-dismiss">
              Got it
            </Button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return null;
}
